import React from 'react';

import type { ActivityViewProps } from '../../core/types/activity.types';

import { CrosswordStage, getTurnLabel } from './CrosswordVisuals';

import { AnagramStage, ChainStage, DrawGuessStage } from './CoopExtraVisuals';

import { useCoopTurnInit } from './useCoopTurnInit';



function HangmanShell({ title, children }: { title: string; children: React.ReactNode }) {

  return (

    <div

      className="rounded-2xl p-6"

      style={{

        background: 'var(--plugin-bg)',

        border: '1px solid var(--plugin-border)',

        color: 'var(--plugin-text)',

      }}

    >

      <h3 className="font-display text-xl font-bold">{title}</h3>

      {children}

    </div>

  );

}



function useCoopAct(props: ActivityViewProps<'coop'>) {

  const { runtime, team, participantId } = props;

  const canAct = team

    ? runtime.currentTurn?.teamId === team.teamId

    : runtime.currentTurn?.participantId === participantId;

  return canAct;

}



export function CoopHostView(props: ActivityViewProps<'coop'>) {

  const { payload, runtime, dispatch, phase } = props;

  const turnUnit = payload.settings?.turnUnit ?? 'team';

  const waiting = phase === 'PREPARING' || phase === 'LOBBY';



  useCoopTurnInit({ payload, runtime, phase, dispatch });



  if (payload.board.kind === 'crossword') {

    return (

      <CrosswordStage

        board={payload.board}

        runtime={runtime}

        title={payload.title}

        badge={waiting ? 'Preparando' : 'Co-op · Crucigrama'}

        size="md"

        turnLabel={getTurnLabel(runtime, turnUnit)}

        dispatch={dispatch}

        readOnly={false}

      />

    );

  }



  if (payload.board.kind === 'chain') {

    return <ChainStage payload={payload as never} runtime={runtime} size="md" />;

  }



  if (payload.board.kind === 'anagram') {

    return <AnagramStage payload={payload as never} runtime={runtime} />;

  }



  if (payload.board.kind === 'draw-guess') {

    return <DrawGuessStage payload={payload as never} runtime={runtime} size="md" />;

  }



  if (payload.board.kind === 'hangman') {

    return (

      <HangmanShell title={payload.title}>

        <p className="mt-2 text-sm" style={{ color: 'var(--plugin-muted)' }}>

          Errores: {runtime.errors ?? 0} / {payload.board.maxErrors ?? 6}

        </p>

        <button

          type="button"

          className="mt-4 rounded-xl px-4 py-2 text-sm font-bold"

          style={{ background: '#14b8a6', color: '#fff' }}

          onClick={() => dispatch({ type: 'ADVANCE_TURN' })}

        >

          Pasar turno

        </button>

      </HangmanShell>

    );

  }



  return <HangmanShell title={payload.title}>Tablero no soportado</HangmanShell>;

}



export function CoopStudentView(props: ActivityViewProps<'coop'>) {

  const { payload, runtime, dispatch, phase, team, participantId } = props;

  const turnUnit = payload.settings?.turnUnit ?? 'team';

  const waiting = phase === 'PREPARING' || phase === 'LOBBY';

  const canAct = useCoopAct(props);



  useCoopTurnInit({ payload, runtime, phase, dispatch });



  if (payload.board.kind === 'crossword') {

    if (waiting) {

      return (

        <CrosswordStage

          board={payload.board}

          runtime={runtime}

          title={payload.title}

          badge="Crucigrama"

          turnLabel="Espera a que comience la actividad"

          readOnly

        />

      );

    }

    return (

      <CrosswordStage

        board={payload.board}

        runtime={runtime}

        title={payload.title}

        badge={canAct ? 'Tu turno' : 'Observando'}

        canAct={canAct}

        turnLabel={getTurnLabel(runtime, turnUnit)}

        dispatch={dispatch}

      />

    );

  }



  if (waiting) {

    return (

      <HangmanShell title={payload.title}>

        <p className="mt-4 text-center text-sm" style={{ color: 'var(--plugin-muted)' }}>

          Espera a que el profesor pulse <strong>Comenzar</strong>

        </p>

      </HangmanShell>

    );

  }



  if (payload.board.kind === 'chain') {

    return (

      <ChainStage

        payload={payload as never}

        runtime={runtime}

        canAct={canAct}

        dispatch={dispatch}

      />

    );

  }



  if (payload.board.kind === 'anagram') {

    return (

      <AnagramStage

        payload={payload as never}

        runtime={runtime}

        canAct={canAct}

        dispatch={dispatch}

      />

    );

  }



  if (payload.board.kind === 'draw-guess') {

    return (

      <DrawGuessStage

        payload={payload as never}

        runtime={runtime}

        participantId={participantId}

        canAct={phase === 'ACTIVE'}

        dispatch={dispatch}

      />

    );

  }



  return (

    <HangmanShell title={payload.title}>

      {canAct ? (

        <p className="mt-4 text-sm" style={{ color: 'var(--plugin-muted)' }}>Tu turno</p>

      ) : (

        <p className="mt-4 text-sm" style={{ color: 'var(--plugin-muted)' }}>Espera tu turno…</p>

      )}

    </HangmanShell>

  );

}



export function CoopProjectorView(props: ActivityViewProps<'coop'>) {

  const { payload, runtime, phase } = props;

  const turnUnit = payload.settings?.turnUnit ?? 'team';



  if (payload.board.kind === 'crossword') {

    return (

      <CrosswordStage

        board={payload.board}

        runtime={runtime}

        title={payload.title}

        badge={runtime.won ? '¡Completado!' : `En vivo · ${phase ?? 'ACTIVE'}`}

        size="lg"

        turnLabel={getTurnLabel(runtime, turnUnit)}

        readOnly

      />

    );

  }



  if (payload.board.kind === 'chain') {

    return <ChainStage payload={payload as never} runtime={runtime} size="lg" />;

  }



  if (payload.board.kind === 'anagram') {

    return <AnagramStage payload={payload as never} runtime={runtime} />;

  }



  if (payload.board.kind === 'draw-guess') {

    return <DrawGuessStage payload={payload as never} runtime={runtime} size="lg" />;

  }



  return <CoopHostView {...props} />;

}


